/*
 * Pixel Manager — bridge between Horizon theme events and the legacy pixel
 * API (LamodaPixel / WpPixel / GazetaPixel).
 *
 * Behavior is 1:1 with old project (estro-fe-shopify):
 *
 *   onCartAdd         — fired when a product is added to cart (broadcast
 *                       'cart_add' in old). Horizon dispatches 'cart:update'
 *                       with data.source = 'product-form-component' /
 *                       'quick-add' / 'quick-add-component'.
 *
 *   onCartOpen        — fired ONLY when the user opens the cart drawer
 *                       themselves (clicking the cart icon). Old broadcast
 *                       fired only when CartDrawer.open(triggeredBy) had
 *                       a truthy `triggeredBy`. We replicate: listen for
 *                       cart-drawer's dialog:open, but suppress the open
 *                       that is triggered automatically right after add.
 *
 *   onCheckout        — fired ONLY when the user clicks the checkout button
 *                       INSIDE the cart drawer (#CartDrawer-Checkout in old).
 *                       In Horizon the button is rendered by cart-summary.liquid
 *                       (name="checkout"). We restrict to clicks inside the
 *                       <cart-drawer> element.
 *
 *   onFilterResult    — fired when collection filters re-render
 *                       (broadcast 'filtersRendered' → ThemeEvents.filterUpdate).
 *
 *   onPageView        — fired on DOMContentLoaded (always).
 *   onProductPageView — fired on DOMContentLoaded when on product template.
 *   onCollectionPageView — fired on DOMContentLoaded when on collection template.
 *
 * window.theme (template/product/variant/collection) is set inline in
 * layout/theme.liquid. Collection product JSON is embedded per card in
 * snippets/product-card.liquid as <script type="application/json"
 * class="product-card__data">.
 */
class PixelManager extends HTMLElement
{
  constructor ()
  {
    super();

    this.pixels = [];
    this._suppressNextDrawerOpen = false;
    this.addPixels();
    this.initEvents();
  }

  addPixels ()
  {
    if (typeof LamodaPixel !== 'undefined'
      && typeof window.lamodaConfig !== 'undefined'
      && window.lamodaConfig.network
    ) {
      this.pixels.push(new LamodaPixel());
    }

    if (typeof WpPixel !== 'undefined'
      && typeof window.wpConfig !== 'undefined'
      && window.wpConfig.id
    ) {
      this.pixels.push(new WpPixel());
    }

    if (typeof GazetaPixel !== 'undefined'
      && typeof window.gazetaConfig !== 'undefined'
      && window.gazetaConfig.id
    ) {
      this.pixels.push(new GazetaPixel());
    }
  }

  initEvents ()
  {
    // -------- cart_add bridge --------
    // Horizon dispatches 'cart:update' for both add and updates.
    // Treat it as cart_add only when the source identifies an add action
    // (product form / quick-add) and a variantId is present.
    document.addEventListener('cart:update', (event) => {
      const detail = event.detail || {};
      const cart = detail.resource || null;
      const data = detail.data || {};

      if (!cart || data.didError) return;

      const isAddSource = data.source === 'product-form-component'
        || data.source === 'quick-add'
        || data.source === 'quick-add-component';

      if (!isAddSource || !data.variantId) return;

      const item = this.findCartItem(cart, data.variantId);
      if (!item) return;

      // The drawer will auto-open as a result of this add. Suppress the
      // matching dialog:open so onCartOpen does not fire on auto-open
      // (matches old behavior where broadcast('cart_open') only ran when
      // CartDrawer.open() was called with a triggeredBy).
      this._suppressNextDrawerOpen = true;
      setTimeout(() => { this._suppressNextDrawerOpen = false; }, 1500);

      // Old `cart_add` payload was the /cart/add.js response (a Shopify line
      // item shape). Line items inside cart.items have exactly the same
      // fields the pixels read: product_id, product_title, price, vendor,
      // product_type, variant_options, quantity.
      this.emitPixelEvent('onCartAdd', item);
    });

    // -------- cart_open bridge --------
    // Listen for cart drawer dialog opens. Suppress the open that follows
    // a cart add (auto-open).
    this.attachCartDrawerOpenListener();

    // -------- checkout bridge --------
    // Old broadcast happened only from CartDrawer-Checkout click handler.
    // Mirror by restricting to clicks inside <cart-drawer>.
    document.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;
      const button = target.closest('button[name="checkout"], input[name="checkout"]');
      if (!button) return;
      if (!button.closest('cart-drawer')) return;

      this.fetchCart().then(cart => {
        if (cart) this.emitPixelEvent('onCheckout', cart);
      });
    }, true);

    // -------- filtersRendered bridge --------
    document.addEventListener('filter:update', () => {
      // Filter re-renders the grid asynchronously; wait one tick so the new
      // product-card JSON nodes are in the DOM before scraping them.
      setTimeout(() => {
        const products = this.collectGridProducts();
        const collection = (window.theme && window.theme.collection) || null;
        this.emitPixelEvent('onFilterResult', collection, products);
      }, 50);
    });

    // -------- page-view triggers (DOMContentLoaded) --------
    const onReady = () => {
      this.emitPixelEvent('onPageView');

      const tpl = window.theme && window.theme.template;
      if (!tpl || typeof tpl.name !== 'string') return;

      switch (tpl.name) {
        case 'product':
          this.emitPixelEvent('onProductPageView', window.theme.product, window.theme.variant);
          break;
        case 'collection':
          this.emitPixelEvent('onCollectionPageView', window.theme.collection, this.collectGridProducts());
          break;
      }
    };

    if (/complete|interactive|loaded/.test(document.readyState)) {
      setTimeout(onReady, 1);
    } else {
      window.addEventListener('DOMContentLoaded', onReady);
    }
  }

  attachCartDrawerOpenListener ()
  {
    const bind = () => {
      const drawers = document.querySelectorAll('cart-drawer');
      if (!drawers.length) return false;
      drawers.forEach(drawer => {
        if (drawer._pixelOpenBound) return;
        drawer._pixelOpenBound = true;
        // Horizon's DialogComponent emits 'dialog:open' on its host.
        drawer.addEventListener('dialog:open', () => {
          if (this._suppressNextDrawerOpen) {
            this._suppressNextDrawerOpen = false;
            return;
          }
          this.fetchCart().then(cart => {
            if (cart) this.emitPixelEvent('onCartOpen', cart);
          });
        });
      });
      return true;
    };

    if (!bind()) {
      // Cart drawer might mount after this manager. Retry once on ready.
      if (/complete|interactive|loaded/.test(document.readyState)) {
        setTimeout(bind, 50);
      } else {
        window.addEventListener('DOMContentLoaded', () => bind());
      }
    }
  }

  findCartItem (cart, variantId)
  {
    if (!cart || !Array.isArray(cart.items)) return null;
    const vid = String(variantId);
    return cart.items.find(item => String(item.variant_id || item.id) === vid) || null;
  }

  collectGridProducts ()
  {
    const products = [];
    document.querySelectorAll('product-card .product-card__data').forEach(node => {
      try {
        products.push(JSON.parse(node.textContent || node.innerHTML));
      } catch (e) {
        console.warn('PixelManager: failed to parse product-card JSON', e);
      }
    });
    return products;
  }

  fetchCart ()
  {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(r => r.ok ? r.json() : null)
      .catch(e => {
        console.warn('PixelManager: /cart.js fetch failed', e);
        return null;
      });
  }

  emitPixelEvent (method, ...vars)
  {
    if (!this.pixels.length) return;

    this.pixels.forEach(pixel => {
      console.info('📌 Pixel event', method, ...vars);

      if (typeof pixel[method] === 'function') {
        try {
          pixel[method](...vars);
        } catch (e) {
          console.error('Pixel failed "' + method + '" method', e);
        }
      } else {
        console.error('Pixels has no "' + method + '" method', pixel);
      }
    });
  }
}

if (!customElements.get('pixel-manager')) {
  customElements.define('pixel-manager', PixelManager);
}
